import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Container from "@/components/marketing/Container";
import HeroBackground from "@/components/marketing/HeroBackground";
import { formatPrice } from "@/lib/utils";

/**
 * Hero section for the per-outlet landing page. Optimised for the keyword
 * "Guaranteed Placement & Guest Post on [outlet] for $X" and for conversion
 * (prominent "Create Free Account" CTA).
 */
export default function OutletDetailHero({ outlet, price, loading, priceWord = "for" }) {
  const [logoError, setLogoError] = useState(false);
  const { name, logo, sector, region, da, tier, staff_written } = outlet;
  const priceDisplay = price != null ? `$${formatPrice(price)}` : null;

  return (
    <section className="relative bg-frame overflow-hidden">
      <HeroBackground />
      <Container className="relative pt-20 pb-16 lg:pt-28 lg:pb-20 text-center">
        {/* Outlet logo */}
        <div className="flex items-center justify-center h-20 mb-8">
          {logo && !logoError ? (
            <div
              className="rounded-xl px-[22px] py-[14px] shadow-md overflow-hidden"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: "fit-content",
                background: "rgba(255,255,255,0.96)",
              }}
            >
              <img
                src={logo}
                alt={`${name} logo`}
                className="h-20 w-auto max-w-[280px] object-contain"
                onError={() => setLogoError(true)}
              />
            </div>
          ) : (
            <span className="font-heading text-3xl font-bold text-white">{name}</span>
          )}
        </div>

        {/* Badges */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-6">
          <span className="inline-block text-[11px] font-medium px-3 py-1 rounded-full bg-cobalt/15 text-lilac border border-cobalt/20">DA {da}</span>
          <span className="inline-block text-[11px] font-medium px-3 py-1 rounded-full bg-white/5 text-mist border border-white/10">{sector}</span>
          {region && <span className="inline-block text-[11px] font-medium px-3 py-1 rounded-full bg-white/5 text-mist border border-white/10">{region}</span>}
          {tier && <span className="inline-block text-[11px] font-medium px-3 py-1 rounded-full bg-white/5 text-mist border border-white/10">{tier}</span>}
          {staff_written && <span className="inline-block text-xs font-semibold px-3.5 py-1.5 rounded-full bg-cobalt text-white">Staff-Written Editorial</span>}
        </div>

        {/* H1 — includes both target keywords + price */}
        <h1 className="font-heading text-3xl sm:text-4xl lg:text-[44px] font-bold text-white leading-[1.15] mb-5 max-w-3xl mx-auto">
          {staff_written ? "Staff-Written Feature" : "Guaranteed Placement & Guest Post"} on {name}
          {priceDisplay && <span className="text-lilac"> {priceWord} {priceDisplay}</span>}
        </h1>

        {/* Subtitle */}
        <p className="text-mist text-base lg:text-lg max-w-2xl mx-auto mb-10 leading-relaxed">
          {staff_written
            ? `A feature on ${name} (DA ${da}) written by the publication's editorial staff. Guaranteed publication, indexed in search, with a backlink to your site. Published or refunded.`
            : `Publish a guest post or press release on ${name} (DA ${da}). Guaranteed publication, indexed in search, with a backlink to your site. Published or refunded.`}
        </p>

        {/* Editorial review disclaimer */}
        <p className="text-faded text-xs max-w-xl mx-auto mb-6 leading-relaxed">
          All placements are subject to editorial review. The publication may label content as advertorial or decline the story, in which case a full refund is issued.
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link to="/register">
            <Button size="lg" className="bg-cobalt hover:bg-cobalt/90 text-white font-medium px-8 py-6 text-base">
              Create Free Account to Order <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
          <Link to="/contact">
            <Button variant="outline" size="lg" className="border-white text-white bg-transparent hover:bg-white/10 hover:text-white px-8 py-6 text-base">
              Talk to Our Team
            </Button>
          </Link>
        </div>

        {!loading && !priceDisplay && (
          <p className="text-faded text-xs mt-6">Pricing varies by content type. Create an account to see live pricing.</p>
        )}
      </Container>
    </section>
  );
}